import type { ICategory } from "@/props/IProduct";
import FilterTitle from "./FilterTitle";
import { useTranslation } from "next-i18next";
import { getCategoryApi } from "@/api/product";
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import Icon from "../Icon";
import useSWR from "swr";

interface CategoryFilterProps {
  onInit?: (data: ICategory[]) => void;
  onChange: (brandId: string) => void;
  hideTitle?: boolean;
  badge?: number;
}

// fetcher 函数，调用接口获取分类数据
const fetcher = async (): Promise<ICategory[]> => {
  const res: any = await getCategoryApi();
  return res;
};

const CategoryFilter = ({ onInit, onChange, hideTitle = false, badge }: CategoryFilterProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const currentLocale: string | undefined =
    router.locale?.toUpperCase() || "EN";

  // 使用 useSWR 请求分类数据
  const { data, error } = useSWR<ICategory[]>("categories", fetcher);

  // 本地保存处理后的分类树数据
  const [categories, setCategories] = useState<ICategory[]>([]);

  // cid 用于存储已选分类 id
  let cid: number[] = [];

  // loading 判断
  const loading = !data && !error;

  /**
   * 格式化分类数据，增加 checked、expand、level 字段
   */
  const initFormatCategories = (data: ICategory[], level = 0) => {
    const initCid: string | string[] | undefined = router.query.cid;
    const tempData = JSON.parse(JSON.stringify(data));
    const callback = (item: ICategory, level: number) => {
      (item.child || (item.child = [])).forEach((v) => {
        callback(v, level + 1);
      });
      item.name = JSON.parse(item.name);
      if (initCid && Number(initCid) === item.id) {
        item.checked = true;
        item.expand = true;
      } else {
        item.checked = false;
        item.expand = false;
      }
      item.level = level;
    };
    tempData.forEach((item) => callback(item, level));
    return tempData;
  };

  /**
   * 设置默认选中状态，展开父级
   */
  const initSetCategories = (data: ICategory[]) => {
    const tempData = JSON.parse(JSON.stringify(data));
    const callback = (item: ICategory, parentItem?: ICategory, i?: number) => {
      (item.child || (item.child = [])).forEach((v) => {
        callback(v, item, i);
      });

      if (item.checked) {
        if (item.child) {
          handleCheckAll(item.child, true);
        }
        if (parentItem) {
          parentItem.expand = true;
        }
        if (item.level === 2 && i !== undefined) {
          tempData[i].expand = true;
        }
      }
    };
    tempData.forEach((item: ICategory, i) => {
      callback(item, undefined, i);
    });
    cid = [];
    getCheckedKeys(tempData);
    onChange(cid.join(","));
    return tempData;
  };

  // 递归获取选中 id
  const getCheckedKeys = (data: ICategory[]) => {
    data.forEach((item: ICategory) => {
      if (item.checked) {
        cid.push(item.id);
      }
      if (item.child) {
        getCheckedKeys(item.child);
      }
    });
  };

  // 处理树节点的展开和选中切换
  const handleTreeCategory = (
    id: number,
    type: "check" | "expand",
    data: ICategory[]
  ) => {
    data.forEach((item: ICategory) => {
      if (item.id === id) {
        if (type === "check") {
          item.checked = !item.checked;
          if (item.child) {
            handleCheckAll(item.child, item.checked);
          }
        } else {
          item.expand = !item.expand;
        }
      } else if (item.child) {
        handleTreeCategory(id, type, item.child);
      }
    });
  };

  // 递归全选或全不选子节点
  const handleCheckAll = (data: ICategory[], checked: boolean) => {
    data.forEach((item: ICategory) => {
      item.checked = checked;
      if (item.child) {
        handleCheckAll(item.child, checked);
      }
    });
  };

  // 处理“更多”展开/收起
  const handleMore = (id: number) => {
    const tempData = JSON.parse(JSON.stringify(categories));
    handleTreeCategory(id, "expand", tempData);
    setCategories(tempData);
  };

  // 处理选中切换
  const handleSelect = (id: number) => {
    const tempData = JSON.parse(JSON.stringify(categories));
    handleTreeCategory(id, "check", tempData);
    cid = [];
    getCheckedKeys(tempData);
    onChange(cid.join(","));
    setCategories(tempData);
  };

  // data 加载成功时格式化数据并初始化选中状态
  useEffect(() => {
    if (!data) return;

    let formatData = initFormatCategories(data);
    if (router.query.cid && typeof router.query.cid === "string") {
      formatData = initSetCategories(formatData);
    }
    onInit && onInit(formatData);
    setCategories(formatData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, router.query.cid]);

  // 渲染单个分类项
  const CategoryItem = ({ category }: { category: ICategory }) => {
    return (
      <>
        <div
          key={category.id}
          className="mb-2 flex cursor-pointer items-center justify-between transition-colors hover:text-primary"
          style={{
            paddingLeft: (category.level ? category.level * 25 : 0) + "px",
          }}
        >
          <div
            id={`checkicon-${category?.name?.EN?.replace(/\s+/g, "")}`}
            className="flex w-[210px] flex-1 items-center"
            onClick={() => handleSelect(category.id)}
            data-qt="mall_category_filter_click"
            data-qt-category-id={category.id}
            data-qt-category-name={category.name?.EN}
            data-qt-checked={category.checked ? 1 : 0}
          >
            {category.checked ? (
              <Icon
                name="checked"
                size={12}
                title="icon check"
                className="mr-3"
              />
            ) : (
              <Icon
                name="check"
                size={12}
                title="icon check"
                className="mr-3"
              />
            )}
            <div className={`mr-3 flex-1 truncate ${category.checked ? "text-stone-800" : "text-gray-400"}`}>
              {category.name[currentLocale]}
            </div>
          </div>

          {category.child && category.child.length > 0 && (
            <>
              {category.expand ? (
                <Icon
                  name="reduce"
                  size={16}
                  title="icon check"
                  className="mr-3 shrink-0"
                  onClick={() => handleMore(category.id)}
                />
              ) : (
                <Icon
                  name="plus"
                  size={16}
                  title="icon check"
                  className="mr-3 shrink-0"
                  onClick={() => handleMore(category.id)}
                />
              )}
            </>
          )}
        </div>
        {category.child &&
          category.expand &&
          category.child.map((item) => (
            <CategoryItem category={item} key={item.id} />
          ))}
      </>
    );
  };

  // Skeleton 占位加载组件
  const SkeletonLoader = () => (
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center">
          <div className="mr-3 h-3 w-3 rounded bg-gray-200" />
          <div className="h-4 w-40 rounded bg-gray-200" />
          {i % 2 === 0 && (
            <div className="ml-auto h-4 w-4 rounded bg-gray-200" />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="mb-8 flex w-full flex-col border-0 border-gray-100 lg:mb-0 lg:border-0 lg:px-0 lg:py-0">
      {!hideTitle && (
        <FilterTitle type="html" title={t("product_category.product_category_title")} badge={badge} />
      )}
      <div className="mb-2 flex flex-col">
        {loading ? (
          <SkeletonLoader />
        ) : (
          categories.map((category) => (
            <CategoryItem category={category} key={category.id} />
          ))
        )}
      </div>
    </div>
  );
};

export default CategoryFilter;
